import { REPORTING_UNITS } from '../constants/reportingUnits'

/**
 * Get the singular form of a reporting unit from its plural form.
 * @param plural - The plural form (e.g., "cases")
 * @returns The singular form (e.g., "case"), or the original value if not found
 */
export function getSingularUnit(plural: string): string {
  const unit = REPORTING_UNITS.find((u) => u.plural === plural)
  return unit?.singular || plural
}

/**
 * Get the plural form of a reporting unit from its singular form.
 * @param singular - The singular form (e.g., "case")
 * @returns The plural form (e.g., "cases"), or the original value if not found
 */
export function getPluralUnit(singular: string): string {
  const unit = REPORTING_UNITS.find((u) => u.singular === singular)
  return unit?.plural || singular
}

/**
 * Get the display name (capitalized singular) of a reporting unit from its plural form.
 * @param plural - The plural form (e.g., "cases")
 * @returns The display name (e.g., "Case", "LB"), or a capitalized version if not found
 */
export function getDisplayName(plural: string): string {
  const unit = REPORTING_UNITS.find((u) => u.plural === plural)
  if (unit?.display_name) {
    return unit.display_name
  }
  // Fallback: capitalize the singular form
  const singular = getSingularUnit(plural)
  return singular.charAt(0).toUpperCase() + singular.slice(1)
}

/**
 * Get the plural display name of a reporting unit from its plural form.
 * @param plural - The plural form (e.g., "cases")
 * @returns The plural display name (e.g., "Cases", "LBS"), or a capitalized version if not found
 */
export function getDisplayNamePlural(plural: string): string {
  const unit = REPORTING_UNITS.find((u) => u.plural === plural)
  if (unit?.display_name_plural) {
    return unit.display_name_plural
  }
  // Fallback: capitalize the plural form
  return plural.charAt(0).toUpperCase() + plural.slice(1)
}

/**
 * Check if a reporting unit value is valid.
 * @param value - The unit value to check
 * @returns true if the value exists in REPORTING_UNITS
 */
export function isValidReportingUnit(value: string): boolean {
  return REPORTING_UNITS.some((u) => u.value === value)
}
